import {
  type EmrPageSource,
  EmrPageSourceSchema,
  IncomingDocumentMimeSchema,
} from "@emr/contracts";
import { z } from "zod";
import { CAPTURED_FILE_LIMITS, EMR_PAGE_MESSAGE } from "./pageCapture";

/**
 * How a captured chart page reaches the BobDoc tab that imports it.
 *
 * Every path keeps the page, and the patient's files the bookmark brought with
 * it, in memory: postMessage from the bookmark to the tab it opened, a
 * BroadcastChannel from that tab to one already waiting, or a paste (which
 * carries the page alone). Nothing here writes them to a URL, browser storage,
 * a log or the network (SECURITY.md).
 */

/** A captured page is a few megabytes at most; anything this large is not a chart. */
export const MAX_CAPTURE_CHARACTERS = 20_000_000;

const FileDetailSchema = z.string().max(4000).nullish();

/**
 * A file the bookmark brought from the other EMR: its bytes, already checked
 * against their type's signature, with that EMR's own filing details as it
 * gave them (telusChr.ts reads them).
 */
const CapturedFileSchema = z.object({
  id: z.string().min(1).max(80),
  name: z.string().min(1).max(1000),
  fileName: z.string().min(1).max(1000),
  mimeType: IncomingDocumentMimeSchema,
  bytes: z
    // Checked by kind rather than instanceof: the bytes may come from another window.
    .custom<ArrayBuffer>(
      (value) => Object.prototype.toString.call(value) === "[object ArrayBuffer]",
    )
    .refine(
      (bytes) => bytes.byteLength > 0 && bytes.byteLength <= CAPTURED_FILE_LIMITS.fileBytes,
      "A brought file is empty or too large",
    ),
  comment: z.string().max(20_000).nullish(),
  subType: FileDetailSchema,
  reportSubclass: FileDetailSchema,
  sourceFacility: FileDetailSchema,
  createdAt: FileDetailSchema,
  addedAt: FileDetailSchema,
  receivedAt: FileDetailSchema,
  collectedAt: FileDetailSchema,
  reviewedAt: FileDetailSchema,
  archived: z.boolean().optional(),
});
export type CapturedFile = z.infer<typeof CapturedFileSchema>;

/**
 * The patient's files, as the bookmark brought them: those it downloaded and
 * those it left behind (with why), or why it could not have CHR's list at all.
 */
const CapturedFilesSchema = z.union([
  z
    .object({
      items: z.array(CapturedFileSchema).max(CAPTURED_FILE_LIMITS.files),
      skipped: z
        .array(z.object({ name: z.string().max(1000), reason: z.string().max(500) }))
        .max(5000),
    })
    .refine(
      (files) =>
        files.items.reduce((total, file) => total + file.bytes.byteLength, 0) <=
        CAPTURED_FILE_LIMITS.totalBytes,
      "The brought files are larger than one send may carry",
    ),
  z.object({ failed: z.string().min(1).max(500) }),
]);
export type CapturedFiles = z.infer<typeof CapturedFilesSchema>;

export interface EmrPageCapture {
  readonly source: EmrPageSource;
  readonly capturedAt: string;
  readonly html: string;
  /** How it reached BobDoc: handed over by the bookmark, or pasted into the import box. */
  readonly via: "bookmark" | "paste";
  /** The patient's files, from a bookmark that brings them; never on a pasted page. */
  readonly files?: CapturedFiles;
}

const CaptureMessageSchema = z.object({
  type: z.literal(EMR_PAGE_MESSAGE.capture),
  version: z.literal(1),
  source: EmrPageSourceSchema,
  capturedAt: z.string().datetime({ offset: true }),
  html: z.string().min(1).max(MAX_CAPTURE_CHARACTERS),
  files: CapturedFilesSchema.optional(),
});

/**
 * Where each EMR serves its pages. TELUS CHR gives every clinic its own
 * subdomain of inputhealth.com (`https://<clinic>.inputhealth.com/app`, seen
 * October 2026). A capture from any other origin is ignored.
 */
const SOURCE_ORIGINS: Readonly<Record<EmrPageSource, readonly RegExp[]>> = {
  "telus-chr": [/^https:\/\/[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.inputhealth\.com$/],
};
/** Local development serves a synthetic chart page from loopback. */
const LOOPBACK_ORIGIN = /^http:\/\/(?:127\.0\.0\.1|localhost)(?::\d{1,5})?$/;

export interface CaptureOriginPolicy {
  /** Accept captures from loopback pages too (local development only). */
  readonly allowLoopback: boolean;
}

export function captureOriginAllowed(
  source: EmrPageSource,
  origin: string,
  policy: CaptureOriginPolicy,
): boolean {
  return (
    SOURCE_ORIGINS[source].some((pattern) => pattern.test(origin)) ||
    (policy.allowLoopback && LOOPBACK_ORIGIN.test(origin))
  );
}

/**
 * The capture in a message the bookmark posted, or null when the event is
 * anything else: not a capture, malformed, or from an origin that does not
 * serve the EMR the capture names.
 */
export function captureFromMessage(
  event: Pick<MessageEvent, "data" | "origin">,
  policy: CaptureOriginPolicy,
): EmrPageCapture | null {
  const parsed = CaptureMessageSchema.safeParse(event.data);
  if (!parsed.success || !captureOriginAllowed(parsed.data.source, event.origin, policy)) {
    return null;
  }
  const { source, capturedAt, html, files } = parsed.data;
  return { source, capturedAt, html, via: "bookmark", ...(files === undefined ? {} : { files }) };
}

export type PastedCapture =
  | { readonly capture: EmrPageCapture }
  | { readonly error: "empty" | "not_a_chart" };

/**
 * What a paste into the import box holds: the capture the bookmark copied when
 * it could not open BobDoc, or, failing that, the HTML a browser copies from a
 * selected page. A copied page is taken as coming from the EMR being imported.
 */
export function captureFromPaste(
  data: Pick<DataTransfer, "getData"> | null,
  source: EmrPageSource,
  now: () => Date = () => new Date(),
): PastedCapture {
  const text = data?.getData("text/plain").trim() ?? "";
  if (text.startsWith("{")) {
    try {
      const parsed = CaptureMessageSchema.safeParse(JSON.parse(text));
      if (parsed.success) {
        const { capturedAt, html } = parsed.data;
        return { capture: { source: parsed.data.source, capturedAt, html, via: "paste" } };
      }
    } catch {
      // Not the bookmark's copy; fall through to the page's own HTML.
    }
  }
  const html = data?.getData("text/html") ?? "";
  if (html.trim().length > 0 && html.length <= MAX_CAPTURE_CHARACTERS) {
    return { capture: { source, capturedAt: now().toISOString(), html, via: "paste" } };
  }
  return { error: text.length === 0 && html.length === 0 ? "empty" : "not_a_chart" };
}

/** Tells the page that opened this tab it may send the capture now. Carries nothing else. */
export function announceReady(opener: Pick<Window, "postMessage">): void {
  opener.postMessage({ type: EMR_PAGE_MESSAGE.ready, version: 1 }, "*");
}

/** Tells the page the capture arrived, addressed to that page's origin only. */
export function acknowledgeCapture(opener: Pick<Window, "postMessage">, origin: string): void {
  opener.postMessage({ type: EMR_PAGE_MESSAGE.received, version: 1 }, origin);
}

// ---------------------------------------------------------------------------
// Between BobDoc tabs
// ---------------------------------------------------------------------------

const RELAY_CHANNEL = "bobdoc-emr-page-import";

const RelayedCaptureSchema = z.object({
  source: EmrPageSourceSchema,
  capturedAt: z.string().datetime({ offset: true }),
  html: z.string().min(1).max(MAX_CAPTURE_CHARACTERS),
  via: z.enum(["bookmark", "paste"]),
  files: CapturedFilesSchema.optional(),
});

type RelayMessage =
  | { readonly type: "offer"; readonly offerId: string }
  | { readonly type: "claim"; readonly offerId: string; readonly claimId: string }
  | {
      readonly type: "deliver";
      readonly offerId: string;
      readonly claimId: string;
      readonly capture: EmrPageCapture;
    }
  | { readonly type: "delivered"; readonly offerId: string; readonly claimId: string };

export interface RelayChannel {
  postMessage(message: RelayMessage): void;
  addEventListener(type: "message", listener: (event: { data: unknown }) => void): void;
  removeEventListener(type: "message", listener: (event: { data: unknown }) => void): void;
  close(): void;
}

export type RelayChannelFactory = () => RelayChannel | null;

/** The browser's channel between this origin's tabs, or null where there is none. */
export const browserRelayChannel: RelayChannelFactory = () =>
  typeof BroadcastChannel === "function"
    ? (new BroadcastChannel(RELAY_CHANNEL) as unknown as RelayChannel)
    : null;

function relayMessage(data: unknown): RelayMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const message = data as Partial<RelayMessage>;
  return typeof message.type === "string" && typeof message.offerId === "string"
    ? (message as RelayMessage)
    : null;
}

/**
 * Offers a capture to a BobDoc tab that is waiting for one (an import box
 * opened from Register patient or Add walk-in) and hands it to the first tab
 * that claims it. Resolves false when no tab claims it or takes delivery in
 * time, so the caller imports it here instead.
 */
export function relayCaptureToWaitingTab(
  capture: EmrPageCapture,
  options: {
    readonly channel?: RelayChannelFactory | undefined;
    readonly claimWindowMs?: number;
    readonly deliveryTimeoutMs?: number;
  } = {},
): Promise<boolean> {
  const channel = (options.channel ?? browserRelayChannel)();
  if (channel === null) return Promise.resolve(false);
  const offerId = crypto.randomUUID();
  return new Promise((resolve) => {
    let claimant: string | null = null;
    let timer = 0;
    const finish = (delivered: boolean) => {
      window.clearTimeout(timer);
      channel.removeEventListener("message", listen);
      channel.close();
      resolve(delivered);
    };
    const listen = (event: { data: unknown }) => {
      const message = relayMessage(event.data);
      if (message === null || message.offerId !== offerId) return;
      if (message.type === "claim" && claimant === null) {
        claimant = message.claimId;
        window.clearTimeout(timer);
        timer = window.setTimeout(() => finish(false), options.deliveryTimeoutMs ?? 3000);
        channel.postMessage({ type: "deliver", offerId, claimId: claimant, capture });
      } else if (message.type === "delivered" && message.claimId === claimant) {
        finish(true);
      }
    };
    channel.addEventListener("message", listen);
    timer = window.setTimeout(() => finish(false), options.claimWindowMs ?? 800);
    channel.postMessage({ type: "offer", offerId });
  });
}

/**
 * While an import box waits, claims each capture another BobDoc tab offers and
 * passes it on (a page that held no chart leaves the box waiting for the next
 * one). Returns the function that stops waiting.
 */
export function waitForRelayedCapture(
  onCapture: (capture: EmrPageCapture) => void,
  options: { readonly channel?: RelayChannelFactory | undefined } = {},
): () => void {
  const channel = (options.channel ?? browserRelayChannel)();
  if (channel === null) return () => {};
  const claimId = crypto.randomUUID();
  const delivered = new Set<string>();
  const listen = (event: { data: unknown }) => {
    const message = relayMessage(event.data);
    if (message === null || delivered.has(message.offerId)) return;
    if (message.type === "offer") {
      channel.postMessage({ type: "claim", offerId: message.offerId, claimId });
    } else if (message.type === "deliver" && message.claimId === claimId) {
      const capture = RelayedCaptureSchema.safeParse(message.capture);
      if (!capture.success) return;
      delivered.add(message.offerId);
      channel.postMessage({ type: "delivered", offerId: message.offerId, claimId });
      const { files, ...page } = capture.data;
      onCapture({ ...page, ...(files === undefined ? {} : { files }) });
    }
  };
  channel.addEventListener("message", listen);
  return () => {
    channel.removeEventListener("message", listen);
    channel.close();
  };
}
